var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Prop, Watch } from 'vue-property-decorator';
import PageHeader from '@/components/page-header/PageHeader';
import { Subject } from '@/models/Subject';
import AlertCard from '@/components/alert-card/AlertCard';
import SubjectOverview from '@/components/subject-overview/SubjectOverview';
import SubjectOverviewNew from '@/components/subject-overview-new/SubjectOverviewNew';
import AbstractUrl from '@/support/AbstractUrl';
import SummaryFilters from '@/components/SummaryFilters/SummaryFilters';
import { UserSubjectStatusEnum } from '@/models/UserSubject';
import { debounce, isArray, isEmpty, omitBy } from 'lodash';
import ErrorHandler from '@/support/ErrorHandler';
import client from '@/support/Client';
import DownloadOverviewDialog from '@/components/DownloadOverviewDialog/DownloadOverviewDialog';
import { Label } from '@/models/Label';
import SummaryLegendLabels from './SummaryLegendLabels/SummaryLegendLabels';
import { Group } from '@/models/Group';
import UserGoalNotesDialog from '@/components/UserGoalNotesDialog/UserGoalNotesDialog.vue';
let Summary = class Summary extends AbstractUrl {
    constructor() {
        super(...arguments);
        this.$pageTitle = 'Schedule Subject';
        this.topic = null;
        this.isIntializing = true;
        this.componentType = 'status';
        this.filters = {
            area: '',
            topic: '',
            groups: [],
            periods: [],
            levels: [],
            statuses: [
                UserSubjectStatusEnum.HIGHLIGHTED,
                UserSubjectStatusEnum.LESSON_DONE,
                UserSubjectStatusEnum.BUSY,
                UserSubjectStatusEnum.OLD,
                UserSubjectStatusEnum.CLOSED,
                UserSubjectStatusEnum.FINISHED,
            ],
            updated_at: [],
            trashed: false,
            projects: [],
            exclude_projects: [],
            subject_labels: [],
            exit_profile: '',
            include_past_years: false,
            subject_labels_or: false,
        };
        this.isShowingLegendDialog = false;
        this.debounceFetchLabels = debounce(this.fetchLabels, 100);
        this.debounceFetchTopic = debounce(this.fetchTopic, 100);
        this.isDownloadingOverview = false;
        this.isDisplayingDownloadDialog = false;
        this.downloadOverviewUrl = '';
        this.labels = {};
        this.defaultGoalsetFilter = 'goalset-only';
        this.selectedProjectId = null;
    }
    mounted() {
        this.filters = { ...this.filters, ...this.$route.query };
        // goalset is a UI-only param used to seed the goalset filter; remove it from the
        // payload so it is not forwarded to the backend (which does not recognise it).
        delete this.filters.goalset;
        delete this.filters.projectId;
        if (typeof this.filters.levels === 'string') {
            this.filters.levels = [this.filters.levels];
        }
        if (this.filters && this.filters.subject_labels) {
            if (typeof this.filters.subject_labels === 'string') {
                this.filters.subject_labels = [this.filters.subject_labels];
            }
        }
        if (this.filters && this.filters.subject_labels_or) {
            if (typeof this.filters.subject_labels_or === 'string') {
                this.filters.subject_labels_or = this.filters.subject_labels_or === 'true';
            }
        }
        if (this.filters && this.filters.include_past_years) {
            if (typeof this.filters.include_past_years === 'string') {
                this.filters.include_past_years = this.filters.include_past_years === 'true';
            }
        }
        if (this.$route.query.goalset) {
            const value = this.$route.query.goalset;
            if (value === 'goalset-only') {
                const projectId = this.$route.query.projectId;
                if (typeof projectId === 'string' && projectId) {
                    this.filters.projects = [projectId];
                }
                else if (this.group.projects) {
                    this.filters.projects = this.group.projects.map((project) => project.id || '');
                }
                this.defaultGoalsetFilter = 'goalset-only';
            }
            else {
                this.filters.projects = [];
                this.defaultGoalsetFilter = 'all-goals';
            }
        }
        if (this.$route.query.projectId) {
            this.selectedProjectId = this.$route.query.projectId;
            this.filters.projects = [this.selectedProjectId];
            this.defaultGoalsetFilter = 'goalset-only';
        }
        this.debounceFetchLabels();
        this.debounceFetchTopic();
        this.$nextTick(() => {
            this.isIntializing = false;
        });
    }
    onToggleComponentState() {
        this.componentType = this.componentType === 'status' ? 'notes' : 'status';
    }
    onAddNoteForAllStudents() {
        if (!this.subjectOverviewNewComponent) {
            return;
        }
        this.subjectOverviewNewComponent.onAddNoteForAllStudents();
    }
    onUpdateFilters() {
        const parsed = omitBy(this.filters, (value) => {
            return (value === null ||
                value === undefined ||
                value === '' ||
                (Array.isArray(value) && value.length === 0));
        });
        if (parsed && parsed.subject_labels && !isArray(parsed.subject_labels)) {
            parsed.subject_labels = [parsed.subject_labels];
        }
        this.setQueryParams(parsed);
    }
    async fetchTopic() {
        if (this.filters.topic) {
            this.topic = await new Subject().find(this.filters.topic);
        }
    }
    get breadcrumbs() {
        return [
            { name: this.$t('summary.title').toString(), path: '/subjects' },
            // { name: this.schedule.name || '', path: this.$route.path }
        ];
    }
    get showSubjectOverview() {
        if (this.needsPeriodAndLevelFilter && !this.hasPeriodAndLevelFilter) {
            return false;
        }
        return true;
    }
    get needsPeriodAndLevelFilter() {
        return !!this.filters.include_past_years;
    }
    get hasPeriodAndLevelFilter() {
        return (!!this.filters.periods &&
            this.filters.periods.length > 0 &&
            !!this.filters.levels &&
            this.filters.levels.length > 0);
    }
    get subjectOverviewNewComponent() {
        return (this.$refs && this.$refs.subjectOverviewNewComponent) || null;
    }
    onGoalSetFilterChanged(value) {
        if (value === 'goalset-only') {
            if (this.group.projects) {
                this.filters.projects = this.group.projects.map((project) => project.id || '');
                this.defaultGoalsetFilter = 'goalset-only';
            }
        }
        else {
            this.filters.projects = [];
            this.defaultGoalsetFilter = 'all-goals';
            this.selectedProjectId = null;
            this.$router
                .replace({ query: { ...this.$route.query, projectId: undefined } })
                .catch((error) => {
                if (error.name !== 'NavigationDuplicated') {
                    throw error;
                }
            });
        }
    }
    onSelectedProjectChanged(projectId) {
        this.selectedProjectId = projectId;
        if (projectId) {
            this.filters.projects = [projectId];
        }
        else if (this.group.projects) {
            this.filters.projects = this.group.projects.map((project) => project.id || '');
        }
        this.$router
            .replace({ query: { ...this.$route.query, projectId: projectId || undefined } })
            .catch((error) => {
            if (error.name !== 'NavigationDuplicated') {
                throw error;
            }
        });
    }
    async onClickDownloadOverview() {
        this.isDownloadingOverview = true;
        try {
            const parsed = omitBy(this.filters, isEmpty);
            const download = await client('get', 'subjects/export-summary', {
                filters: parsed,
                ...{ with: ['parent'] },
            });
            this.isDisplayingDownloadDialog = true;
            this.downloadOverviewUrl = download.data.pdf;
            // window.open(download.data.pdf, '_blank');
        }
        catch (error) {
            if (error.response) {
                ErrorHandler.network(error);
            }
        }
        finally {
            this.isDownloadingOverview = false;
        }
    }
    async fetchLabels() {
        try {
            const labels = await new Label().limit(999).all();
            this.labels = labels.reduce((accumulator, currentLabel) => {
                const { key, value } = currentLabel;
                if (key) {
                    accumulator[key] = value || '';
                }
                return accumulator;
            }, {});
        }
        catch (error) {
            ErrorHandler.network(error);
        }
    }
    onAreaChanged() {
        if (this.isIntializing) {
            return;
        }
        this.filters.subject_labels = [];
        this.onUpdateFilters();
        this.debounceFetchLabels();
        this.debounceFetchTopic();
    }
    onFiltersChanged() {
        if (this.isIntializing || (this.needsPeriodAndLevelFilter && !this.hasPeriodAndLevelFilter)) {
            return;
        }
        this.onUpdateFilters();
        this.debounceFetchLabels();
        this.debounceFetchTopic();
    }
};
__decorate([
    Prop({ default: true }),
    __metadata("design:type", Boolean)
], Summary.prototype, "showFilters", void 0);
__decorate([
    Prop(),
    __metadata("design:type", typeof (_a = typeof Group !== "undefined" && Group) === "function" ? _a : Object)
], Summary.prototype, "group", void 0);
__decorate([
    Watch('filters.area'),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Summary.prototype, "onAreaChanged", null);
__decorate([
    Watch('filters', { deep: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Summary.prototype, "onFiltersChanged", null);
Summary = __decorate([
    Component({
        components: {
            AlertCard,
            PageHeader,
            SummaryFilters,
            SubjectOverview,
            SubjectOverviewNew,
            DownloadOverviewDialog,
            SummaryLegendLabels,
            UserGoalNotesDialog,
        },
    })
], Summary);
export default Summary;
